import { HttpClient } from '@/shared/infra/http/HttpClient';
import { II9FormViewerRepository } from '@/features/i9_form_viewer/application/ports/II9FormViewerRepository';
import { I9FormSections } from '@/features/i9_form_viewer/application/domain/entities/I9FormSections';
import { mapSectionOneDtoToEntity } from '@/features/section-1/infra/repositories/mappers/sectionOneMapper';
import { mapSection2MutationResponseDtoToEntity } from '@/features/section-2/infra/repositories/mappers/section2Mapper';
import { SectionOneResponseDto } from '@/features/section-1/infra/repositories/dto/res/SectionOneResponseDto';
import { Section2ResponseDto } from '@/features/section-2/infra/repositories/dto/res/Section2ResponseDto';
import { BlobDocumentResponseDto } from '@/features/section-2/infra/repositories/dto/res/BlobDocumentResponseDto';
import { Supplement } from '@/features/supplement/infra/repositories/dto/res/SupplementResponseDto';
import type { DocumentUploads } from '@/features/section-2/application/domain/entities/DocumentUploads';

/**
 * Raw API response shape from GET /i9Form/{id}/sections
 */
interface I9FormSectionsApiResponse {
    section1: SectionOneResponseDto | null;
    section2: Section2ResponseDto | null;
    supplementB: Supplement[] | null;
}

/**
 * API implementation of I9 Form Viewer Repository
 * Fetches sections data and maps to domain entities using existing mappers
 */
export class ApiI9FormViewerRepository implements II9FormViewerRepository {
    constructor(private readonly httpClient: HttpClient) { }

    async fetchSections(i9FormId: string): Promise<I9FormSections> {
        const response = await this.httpClient.get<I9FormSectionsApiResponse>(
            `/i9Form/${i9FormId}/sections`
        );

        const apiData = response.data;

        return {
            section1: apiData.section1
                ? mapSectionOneDtoToEntity(apiData.section1)
                : null,
            section2: apiData.section2
                ? mapSection2MutationResponseDtoToEntity(apiData.section2)
                : null,
            supplementB: apiData.supplementB
        };
    }

      async fetchDocumentFiles(i9FormId: string): Promise<DocumentUploads> {
        const response = await this.httpClient.get<DocumentUploads>(
          `/section2/${i9FormId}/document-files`
        );
        return response.data;
      }

      async fetchSupplementBDocumentFiles(i9FormId: string): Promise<DocumentUploads> {
        const response = await this.httpClient.get<DocumentUploads>(
          `/supplement/${i9FormId}/document-files/completed`
        );
        return response.data;
      }

      /**
       * Get Base64 of a document from blob storage
       * Returns base64 encoded document data
       */
      async getBase64Document(documentUrl: string): Promise<string> {
        const response = await this.httpClient.get<BlobDocumentResponseDto>(
          '/blob/document',
          {
            params: {
              url: documentUrl
            },
          }
        );
    
        return response.data.document;
      }
}
